import {
  BrokenCircuitError,
  ConsecutiveBreaker,
  circuitBreaker,
  ExponentialBackoff,
  handleAll,
  handleWhen,
  retry,
  wrap,
} from "cockatiel";

export interface ResilienceOptions {
  breakerThreshold?: number;
  halfOpenAfter?: number;
  maxRetries?: number;
}

/**
 * Wraps a promise-returning function with circuit breaker and exponential
 * backoff retry. The breaker opens after `breakerThreshold` consecutive
 * failures and half-opens after `halfOpenAfter` ms.
 */
export function createResilientCall<TArgs extends unknown[], TReturn>(
  fn: (...args: TArgs) => Promise<TReturn>,
  options: ResilienceOptions = {},
): (...args: TArgs) => Promise<TReturn> {
  const breaker = circuitBreaker(handleAll, {
    halfOpenAfter: options.halfOpenAfter ?? 30_000,
    breaker: new ConsecutiveBreaker(options.breakerThreshold ?? 5),
  });

  // The breaker is the inner policy, so once it is open it surfaces
  // `BrokenCircuitError` (and `IsolatedCircuitError`) without calling `fn`.
  // Retrying those would burn the whole retry budget on exponential backoff
  // instead of failing fast, so the retry filter excludes circuit-open errors
  // and only retries genuine failures coming back from `fn`.
  const retryPolicy = retry(
    handleWhen((error) => !(error instanceof BrokenCircuitError)),
    {
      maxAttempts: options.maxRetries ?? 3,
      backoff: new ExponentialBackoff(),
    },
  );

  // cockatiel's `wrap(a, b)` runs `a` as the outer policy, so retry must be
  // outer and the breaker inner: every attempt then passes through the breaker
  // and it opens after `breakerThreshold` failed attempts. The reverse order
  // makes the breaker count whole retry sequences, so it trips only after
  // `breakerThreshold * (maxRetries + 1)` calls and lets each request fire a
  // full retry storm at a failing backend before it can short-circuit.
  const policy = wrap(retryPolicy, breaker);
  return (...args: TArgs) => policy.execute(() => fn(...args));
}
